import { InboxCount, LoadingInboxCount } from 'pages/inbox';
import { LoadingTodayCount, TodayCount } from 'pages/today';
import React, { Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigationSectionLayout } from 'shared/components/ui/navigation-section-layout';
import { SideNavigationItem } from 'shared/components/ui/side-navigation-item';
import { useUser } from 'shared/hooks/use-user';
import { Paths } from 'shared/routes';
import {
  MainNavigationItemId,
  SubNavigationItem,
} from 'shared/routes/navigation';

import * as Styled from './side-navigation.style';

type SubItemProps = SubNavigationItem & {
  activePath?: Paths;
  onClick: () => void;
};

export const SubItem: React.FC<SubItemProps> = ({
  link,
  id,
  pathsToMatch,
  comingSoon,
  icon,
  onClick,
  activePath,
  translationLabel,
}) => {
  const { t } = useTranslation();

  const active = !!activePath && !!pathsToMatch?.includes(activePath);

  return (
    <SideNavigationItem
      label={t(translationLabel)}
      onClick={onClick}
      link={link!}
      active={active}
      icon={icon}
    >
      {comingSoon && (
        <Styled.ComingSoon>{t('general.labels.coming-soon')}</Styled.ComingSoon>
      )}

      {id === MainNavigationItemId.Today && (
        <Suspense fallback={<LoadingTodayCount />}>
          <TodayCount />
        </Suspense>
      )}
      {id === MainNavigationItemId.Inbox && (
        <Suspense fallback={<LoadingInboxCount />}>
          <InboxCount />
        </Suspense>
      )}
    </SideNavigationItem>
  );
};

export type NavigationSectionProps = {
  translationLabel: string;
  initialOpen?: boolean;
  subItems: SubNavigationItem[];
  onNavigationItem: () => void;
  activePath?: Paths;
  isFirst?: boolean;
};

export const NavigationSection: React.FC<NavigationSectionProps> = ({
  translationLabel,
  initialOpen,
  subItems,
  onNavigationItem,
  activePath,
  isFirst,
}) => {
  const { t } = useTranslation();
  const user = useUser();

  const navItems = useMemo(() => {
    if (user?.email.endsWith('@griply.app')) {
      return subItems;
    }
    // For all other users, filter out GoalsCalendar
    return subItems.filter(
      ({ id }) => id !== MainNavigationItemId.AllTasksFiltered,
    );
  }, [subItems, user?.email]);

  return (
    <NavigationSectionLayout
      label={t(translationLabel)}
      isFirst={isFirst}
      initialOpen={initialOpen}
    >
      <Styled.SubNavigationList>
        {navItems.map((subItem) => (
          <Styled.SubNavigationListItem key={subItem.id}>
            <SubItem
              id={subItem.id}
              link={subItem.link}
              activePath={activePath}
              pathsToMatch={subItem.pathsToMatch}
              translationLabel={subItem.translationLabel}
              icon={subItem.icon}
              comingSoon={subItem.comingSoon}
              onClick={onNavigationItem}
            />
          </Styled.SubNavigationListItem>
        ))}
      </Styled.SubNavigationList>
    </NavigationSectionLayout>
  );
};
